// ** React Imports
import { useContext, useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import styled from "styled-components";
import loadable from "@loadable/component";
import withHydrationOnDemand from "react-hydration-on-demand";

// ** Custom Components
import Block from "components/layout/Block";
import EmptyBlock from "components/layout/Block/BlockEmpty";

// ** Custom Hooks
import { useMemoizedUser } from "hooks/useMeMoizedUser";

// ** User Components
const Gallery = loadable(() => import("./components/Gallery"));
const GalleryWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: Gallery.load,
})(Gallery);
const UserProfileInfo = loadable(() => import("./components/UserProfileInfo"));
const UserProfileInfoWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: UserProfileInfo.load,
})(UserProfileInfo);
const UserProfileInfoMobile = loadable(() =>
	import("./components/UserProfileInfoMobile")
);
const UserProfileInfoMobileWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: UserProfileInfoMobile.load,
})(UserProfileInfoMobile);
const UserProfileMedia = loadable(() =>
	import("./components/UserProfileMedia")
);
const UserProfileMediaWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: UserProfileMedia.load,
})(UserProfileMedia);

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import { StyledLeftSection } from "./styles";

// #####################################################

const StyledProfileColumnWrapper = styled(({ ...rest }) => <Block {...rest} />)`
	gap: var(--size-grid);
`;

// #####################################################

const Profile = ({ login }) => {
	const { isPhone, isTablet, isDesktop } = useContext(deviceTypeContext);
	const user = useMemoizedUser();

	const galleryLoaded = useSelector(
		(state) => state.userProfileMainContent.gallery.loaded
	);
	const galleryData = useSelector(
		(state) => state.userProfileMainContent.gallery.data
	);

	const isGalleryEmpty =
		galleryLoaded &&
		(galleryData?.length === 0 || !galleryData?.length === 0);

	const wrapperProps = useMemo(() => {
		if (isDesktop) {
			return { style: { flex: 1 } };
		}
		return {};
	}, [isDesktop]);

	// #####################################################

	return (
		<StyledProfileColumnWrapper flex column={isTablet && !isDesktop}>
			<StyledLeftSection>
				{isPhone ? (
					<UserProfileInfoMobileWithHydrationOnDemand user={user} />
				) : (
					<UserProfileInfoWithHydrationOnDemand user={user} />
				)}

				{!isGalleryEmpty && (
					<GalleryWithHydrationOnDemand
						galleryLoaded={galleryLoaded}
						galleryData={galleryData}
						login={user.login || login}
					/>
				)}
				{isDesktop && <EmptyBlock />}
			</StyledLeftSection>

			<UserProfileMediaWithHydrationOnDemand
				wrapperProps={wrapperProps}
				user={user}
			/>
		</StyledProfileColumnWrapper>
	);
};

// #####################################################

export default Profile;

// #####################################################
